import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { MobileScrollRail } from './MobileScrollRail';

// Fallback static jobs data
const fallbackJobs = [
  {
    id: 1,
    title: 'AI GAME DESIGNER',
    role: 'Game Design',
    location: 'Remote / Jeddah',
    type: 'Full-time',
    experience: 'Mid-level',
    description: 'Create innovative AI-driven gameplay features that push the boundaries of interactive horror storytelling. Work with cutting-edge AI tools to develop dynamic narrative systems.',
    requirements: ['3+ years game design experience', 'AI/ML knowledge preferred', 'Unity/Unreal Engine experience', 'Portfolio required'],
    isNew: true
  },
  {
    id: 2,
    title: 'NARRATIVE DESIGNER',
    role: 'Story Development',
    location: 'Remote / Jeddah',
    type: 'Full-time',
    experience: 'Mid-level',
    description: 'Develop immersive and spine-chilling narratives that guide players through mysterious environments. Create branching storylines and character development systems.',
    requirements: ['Creative writing background', 'Game narrative experience', 'Horror genre expertise', 'Collaborative mindset'],
    isNew: true
  },
  {
    id: 3,
    title: '3D ARTIST',
    role: 'Art & Design',
    location: 'Remote / Jeddah',
    type: 'Full-time',
    experience: 'Mid-level',
    description: 'Create stunning 3D environments, characters, and assets for our horror games. Bring terrifying visions to life with exceptional attention to detail.',
    requirements: ['3D modeling expertise', 'Texturing and lighting skills', 'Maya/Blender proficiency', 'Horror aesthetic sense'],
    isNew: false
  },
  {
    id: 4,
    title: 'SOUND DESIGNER',
    role: 'Audio',
    location: 'Remote / Jeddah',
    type: 'Full-time',
    experience: 'Mid-level',
    description: 'Design and implement atmospheric audio that enhances the horror experience. Create immersive soundscapes that keep players on edge.',
    requirements: ['Audio production skills', 'Game audio experience', 'Foley and music composition', 'Technical audio knowledge'],
    isNew: false
  },
  {
    id: 5,
    title: 'GAME PROGRAMMER',
    role: 'Engineering',
    location: 'Remote / Jeddah',
    type: 'Full-time',
    experience: 'Senior',
    description: 'Develop core game systems and mechanics for our horror titles. Work on AI systems, physics, and performance optimization.',
    requirements: ['C++/C# proficiency', 'Game engine experience', 'AI programming knowledge', 'Performance optimization skills'],
    isNew: false
  }
];

export default function JobList({ careers = [] }) {
  const [selectedJob, setSelectedJob] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const scrollContainerRef = useRef(null);
  const navigate = useNavigate();

  // Transform Firestore careers data to match component structure
  const jobs = useMemo(() => {
    if (careers.length > 0) {
      return careers.map(career => ({
        id: career.id,
        title: career.jobTitle?.toUpperCase() || 'POSITION',
        role: career.department || 'General',
        location: career.location || 'Remote',
        type: career.type || 'Full-time',
        experience: career.experience || 'Mid-level',
        description: career.description || 'Join our creative team and help us build amazing experiences.',
        requirements: career.requirements ? career.requirements.split(',').map(req => req.trim()) : ['Experience in relevant field'],
        isNew: true // Mark all Firestore jobs as new
      }));
    }
    return fallbackJobs;
  }, [careers]);

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setShowModal(true);
  };

  const handleApplyNow = (job) => {
    navigate('/application', { state: { selectedJob: job } });
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedJob(null);
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      // Get the width of one card plus gap
      const cardWidth = window.innerWidth <= 480 ? 250 : window.innerWidth <= 768 ? 280 : 350;
      const gap = 85; // 2rem = 32px
      const amount = cardWidth + gap;
      
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -amount : amount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <div className="job-list-container">
        <button className="scroll-arrow scroll-left" onClick={() => scroll('left')}>
          <FaChevronLeft />
        </button>

        <div className="job-list" ref={scrollContainerRef}>
          {jobs.map((job) => (
            <div className="job-card" key={job.id}>
              <div className="job-header">
                <div className="job-title-section">
                  <h4>{job.title}</h4>
                  {job.isNew && <span className="new-badge">NEW</span>}
                </div>
                <div className="job-meta">
                  <span className="job-type">{job.type}</span>
                  <span className="job-experience">{job.experience}</span>
                </div>
              </div>
              
              <div className="job-info">
                <p className="job-role"><strong>Department:</strong> {job.role}</p>
                <p className="job-location"><strong>Location:</strong> {job.location}</p>
                <p className="job-description">{job.description}</p>
              </div>

              <div className="job-actions">
                <button 
                  className="job-btn primary" 
                  onClick={() => handleApplyNow(job)}
                >
                  Apply Now
                </button>
                <button 
                  className="job-btn secondary" 
                  onClick={() => handleJobClick(job)}
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>

        <MobileScrollRail scrollerRef={scrollContainerRef} />

        <button className="scroll-arrow scroll-right" onClick={() => scroll('right')}>
          <FaChevronRight />
        </button>
      </div>

      {/* Job Details Modal */}
      {showModal && selectedJob && (
        <div className="job-modal-overlay" onClick={closeModal}>
          <div className="job-modal" onClick={(e) => e.stopPropagation()}>
            <div className="job-modal-header">
              <h3>{selectedJob.title}</h3>
              <button className="close-modal" onClick={closeModal}>&times;</button>
            </div>
            
            <div className="job-modal-content">
              <div className="job-modal-meta">
                <p><strong>Department:</strong> {selectedJob.role}</p>
                <p><strong>Location:</strong> {selectedJob.location}</p>
                <p><strong>Type:</strong> {selectedJob.type}</p>
                <p><strong>Experience:</strong> {selectedJob.experience}</p>
              </div>
              
              <div className="job-modal-description">
                <h4>Job Description</h4>
                <p>{selectedJob.description}</p>
              </div>
              
              <div className="job-modal-requirements">
                <h4>Requirements</h4>
                <ul>
                  {selectedJob.requirements.map((req, index) => (
                    <li key={index}>{req}</li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="job-modal-footer">
              <button 
                className="job-btn primary" 
                onClick={() => handleApplyNow(selectedJob)}
              >
                Apply Now
              </button>
              <button 
                className="job-btn secondary" 
                onClick={closeModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
